import { computed, reactive, ref, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required as requiredRule, email as emailRule } from '@vuelidate/validators'

import type { InputPrimaryProps, InputPrimaryEvent } from '../types/interfaces'

interface InputPrimaryState {
  value: string | number
}

interface UseInputPrimaryOptions {
  props: InputPrimaryProps
  onUpdateValue: (value: InputPrimaryState['value']) => void
  onChange: (event: InputPrimaryEvent) => void
  onInput: (event: InputPrimaryEvent) => void
  onFocus: (event: InputPrimaryEvent) => void
  onBlur: (event: InputPrimaryEvent) => void
  onKeyupEnter: (event: InputPrimaryEvent) => void
}

interface UseInputPrimaryReturn {
  value: Ref<InputPrimaryState['value']>
  v$: ReturnType<typeof useVuelidate<InputPrimaryState>>
  refIsError: ComputedRef<boolean>
  refIsSuccess: ComputedRef<boolean>
  handleChange: () => void
  handleInput: () => void
  handleFocus: () => void
  handleBlur: () => void
  handleKeyupEnter: () => void
}

export const useInputPrimary = ({ props, onUpdateValue, onChange, onInput, onFocus, onBlur, onKeyupEnter }: UseInputPrimaryOptions): UseInputPrimaryReturn => {
  const refIsError = computed<boolean>(() => props.mode === 'error')
  const refIsSuccess = computed<boolean>(() => props.mode === 'success')

  const value = ref<InputPrimaryState['value']>(props.value ?? '')

  watch(() => props.value, (newValue) => {
    if (newValue !== value.value) {
      value.value = newValue ?? ''
    }
  })

  const rules = reactive({
    value: (props.type === 'email')
      ? { requiredRule, emailRule }
      : { requiredRule },
  })
  const v$ = useVuelidate<InputPrimaryState>(rules, { value })

  const isValid = (): boolean => {
    if (props.type === 'email') {
      v$.value.$validate()
      return !v$.value.$invalid
    }

    return props.required
      ? (value.value !== '')
      : true
  }

  const setMinMax = async (): Promise<void> => {
    if (props.type === 'number') {
      if (props.min != null && Number(value.value) <= props.min) {
        value.value = props.min
      }
      if (props.max != null && Number(value.value) >= props.max) {
        value.value = props.max
      }
    }
  }

  const buildEvent = (): InputPrimaryEvent => ({
    id: props.id,
    value: value.value,
    valid: isValid(),
    required: props.required,
  })

  const handleChange = (): void => {
    setMinMax().then(() => {
      onUpdateValue(value.value)
      onChange(buildEvent())
    })
  }

  const handleInput = (): void => {
    onUpdateValue(value.value)
    onInput(buildEvent())
  }

  const handleFocus = (): void => {
    onFocus(buildEvent())
  }

  const handleBlur = (): void => {
    onBlur(buildEvent())
  }

  const handleKeyupEnter = (): void => {
    onKeyupEnter({
      id: props.id,
      value: value.value,
      required: props.required,
    })
  }

  return {
    value,
    v$,
    refIsError,
    refIsSuccess,
    handleChange,
    handleInput,
    handleFocus,
    handleBlur,
    handleKeyupEnter,
  }
}
